import { Injectable, computed, inject, signal } from '@angular/core';

import { PortalFormioAuthApi } from './portal-formio-auth.api';

/**
 * Route-level store responsible for managing the Formio auth token
 * for the portal application.
 *
 * - Fetches a fresh token from the backend when needed
 * - Persists token and expiration in localStorage (for legacy compatibility)
 * - Exposes an `ensureToken()` method that callers can await before
 *   interacting with Formio-powered features.
 */
@Injectable()
export class PortalFormioAuthStore {
  private readonly api = inject(PortalFormioAuthApi);

  private readonly _token = signal<string | null>(this.readTokenFromStorage());
  private readonly _expiration = signal<number | null>(this.readExpirationFromStorage());
  private readonly _loading = signal<boolean>(false);
  private readonly _error = signal<string | null>(null);

  private ongoingRequest: Promise<void> | null = null;

  readonly token = this._token.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly error = this._error.asReadonly();

  readonly isExpired = computed<boolean>(() => {
    const exp = this._expiration();
    if (exp == null || exp <= 0) {
      return true;
    }
    const expSeconds = exp > 1e12 ? Math.floor(exp / 1000) : exp;
    const nowSeconds = Math.floor(Date.now() / 1000);
    const safetyWindowSeconds = 60;
    return nowSeconds >= expSeconds - safetyWindowSeconds;
  });

  /**
   * Ensure there is a non-expired Formio token available.
   * Refreshes when token is missing, empty, or expired. Safe to call multiple times.
   */
  async ensureToken(): Promise<void> {
    const hasValidToken = this._token() && this._token()!.trim() !== '' && !this.isExpired();
    if (hasValidToken) {
      return;
    }

    if (this.ongoingRequest) {
      await this.ongoingRequest;
      return;
    }

    this.ongoingRequest = this.refreshTokenInternal();
    try {
      await this.ongoingRequest;
    } finally {
      this.ongoingRequest = null;
    }
  }

  private async refreshTokenInternal(): Promise<void> {
    this._loading.set(true);
    this._error.set(null);

    try {
      const res = await this.api.refreshFormioToken();

      if (!res || !res.formioToken) {
        throw new Error('Invalid Formio token response');
      }

      this._token.set(res.formioToken);

      const expNumber =
        typeof res.exp === 'string' ? Number.parseInt(res.exp, 10) : typeof res.exp === 'number' ? res.exp : null;
      const expSeconds = Number.isFinite(expNumber ?? NaN)
        ? (expNumber as number) > 1e12
          ? Math.floor((expNumber as number) / 1000)
          : (expNumber as number)
        : null;
      this._expiration.set(expSeconds);

      localStorage.setItem('formioToken', res.formioToken);
      if (this._expiration()) {
        localStorage.setItem('tokenExpiration', String(this._expiration()));
      }
    } catch (err) {
      this._error.set('portal.formioAuth.tokenError');
      this._token.set(null);
      this._expiration.set(null);
      // Intentionally do not rethrow; callers just know token is unavailable
    } finally {
      this._loading.set(false);
    }
  }

  private readTokenFromStorage(): string | null {
    try {
      const value = localStorage.getItem('formioToken');
      return value && value.trim() ? value : null;
    } catch {
      return null;
    }
  }

  private readExpirationFromStorage(): number | null {
    try {
      const raw = localStorage.getItem('tokenExpiration');
      if (!raw) {
        return null;
      }
      const parsed = Number.parseInt(raw, 10);
      return Number.isFinite(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }
}
